import type { ReceiptAddress } from '@matix/types';

/**
 * Render a customer address as the lines that go on the paper.
 *
 * This is the receipt's copy of the cashier's own `addressLabel` — the same
 * field order, the same labelled prefixes, the same Arabic vocabulary — and it
 * is deliberately the SAME rule: the address printed on the slip has to read
 * as the address the cashier picked in the delivery modal, or the person on
 * the phone reading a complaint back has two different documents in front of
 * them.
 *
 * Two things carried over from that function, both load-bearing:
 *
 * - **Widest first.** City, then district, then block / street / avenue /
 *   building / floor / flat. A driver scans from the left until it is specific
 *   enough, and that only works if the order never varies.
 * - **Every field the row carries appears, and every prefix is labelled.**
 *   "12 · 5 · 3" is three numbers; "Block 12 · St 5 · Bldg 3" is an address. A
 *   summary that quietly drops the avenue is indistinguishable, on paper, from
 *   an address that never had one.
 *
 * Returns the lines in print order, or `[]` when the row holds nothing worth
 * printing — the caller renders no address row at all in that case rather than
 * an empty one. The legacy table stores unfilled fields as `''` as often as
 * `NULL`, so every value is trimmed before it counts.
 */
export function formatReceiptAddress(
  addr: ReceiptAddress | null | undefined,
  arabic: boolean,
): string[] {
  if (!addr) return [];

  const pick = (en: string | null | undefined, ar: string | null | undefined): string => {
    const a = (ar ?? '').trim();
    const e = (en ?? '').trim();
    return arabic ? a || e : e || a;
  };
  const label = (en: string, ar: string): string => (arabic ? ar : en);
  const tagged = (tag: string, value: string | null | undefined): string => {
    const v = (value ?? '').trim();
    return v ? `${tag} ${v}` : '';
  };

  // Line 1 — the wide part: city, then district.
  const head = [pick(addr.city, addr.cityAr), pick(addr.district, addr.districtAr)]
    .filter((p) => p.length > 0)
    .join(' · ');

  // Line 2 — the specific part, each number under its own label.
  const detail = [
    tagged(label('Block', 'قطعة'), addr.block),
    tagged(label('St', 'شارع'), addr.street),
    tagged(label('Ave', 'جادة'), addr.avenue),
    tagged(label('Bldg', 'مبنى'), addr.house),
    tagged(label('Fl', 'طابق'), addr.floor),
    tagged(label('Flat', 'شقة'), addr.flat),
  ]
    .filter((p) => p.length > 0)
    .join(' · ');

  // Line 3 — the landmark, verbatim and on its own line. It is a sentence, not
  // a field, and joining it onto the numbers makes both unreadable.
  const note = (addr.note ?? '').trim();

  return [head, detail, note].filter((l) => l.length > 0);
}
